import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { RechargeAddress } from "../portal/types";
import { fetchRechargeAddresses } from "./RechargeApi";

export type RechargeCustomerDetails = {
  hash: string;
  token: string;
  addressess?: RechargeAddress[] | null;
};

const CustomerContext = React.createContext<RechargeCustomerDetails | null>(
  null
);

/**
 * We have recharge customer token in the window global on account-navigation.liquid file
 * This way we don't have to make same 2nd time in React to get the recharge customer token
 */
const fetchRechargeData = async (): Promise<{
  hash: string;
  token: string;
}> => {
  const pollingInterval = 100;
  return new Promise((resolve, reject) => {
    const intervalId = setInterval(() => {
      const data = window.RechargeCustomerInfo;
      if (data) {
        clearInterval(intervalId);
        resolve(data);
      }
    }, pollingInterval);

    // Add a timeout to prevent infinite polling
    setTimeout(() => {
      clearInterval(intervalId);
      reject(new Error("Failed to fetch recharge data in a timely manner."));
    }, 8000); // 8 seconds timeout
  });
};

const useRechargeData = () => {
  return useQuery(["rechargeData"], fetchRechargeData, {
    refetchOnWindowFocus: false, // Do not refetch on window focus
    staleTime: 60000, // 60 seconds stale time
  });
};

const useUserAddresses = (customerHash: string, customerToken: string) => {
  return useQuery(
    ["userAddresses", customerHash, customerToken],
    () => fetchRechargeAddresses(customerHash, customerToken),
    {
      enabled: !!customerHash && !!customerToken, // Enable query only if hash and token are available
      refetchOnWindowFocus: false, // Prevent refetching on window focus
      staleTime: 300000, // Cache data for 5 minutes (300000 milliseconds)
    }
  );
};

export const RechargeCustomerProvider: React.FC<
  React.PropsWithChildren<{}>
> = ({ children }) => {
  const { data: customerData } = useRechargeData();
  const { data: rechargeAddresses } = useUserAddresses(
    customerData?.hash ?? "",
    customerData?.token ?? ""
  );
  const addressess =
    rechargeAddresses && rechargeAddresses.length > 0
      ? rechargeAddresses
      : null;
  const providerData =
    !customerData?.token || !customerData.token
      ? null
      : { ...customerData, addressess };
  return (
    <CustomerContext.Provider value={providerData}>
      {children}
    </CustomerContext.Provider>
  );
};

export function useRechargeCustomer() {
  return useContext(CustomerContext);
}
